/* =============================================================
   OutgoingProfile.jsx — Detailed profile for Tom Baker
   -------------------------------------------------------------
   Hardcoded detailed profile page for the demo outgoing student
   (Tom Baker). Shows the exchange progress steps, document
   checklist, learning agreement status, and admin notes.

   This is a purpose-built demo page. Other outgoing students
   use GenericOutgoingProfile.jsx instead.

   TODO (backend integration):
   - Merge with GenericOutgoingProfile once data comes from Supabase
   - Replace localStorage status with a DB update
   ============================================================= */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useLocalNote } from '../hooks/useLocalNote';
import { useDocuments } from '../context/DocumentContext';

const STUDENT_ID   = 'tom-baker';
const STEP_STORAGE = `unixplore_step_${STUDENT_ID}`;
const STATUS_KEY   = `unixplore_computed_status_outgoing_${STUDENT_ID}`;

/* ─── STEPS & STATUS CONFIG ─────────────────────────────── */
const STEPS = [
  { label: 'Application',    sub: 'Oct–Jan'       },
  { label: 'Nomination R1',  sub: 'Mar'           },
  { label: 'Nomination R2',  sub: 'If applicable' },
  { label: 'Pre-departure',  sub: 'May+'          },
  { label: 'During Exchange',sub: 'Semester'      },
  { label: 'Return',         sub: 'Post'          },
];

const STEP_TO_STATUS = [
  'Applied', 'Nominated', 'Nominated', 'Partner Contacted', 'Departed', 'Returned',
];

const STEP_NOTES = [
  'Review student applications and confirm internal selection.',
  'Submit nomination to partner university — R1 round.',
  'Await partner confirmation — collect acceptance documents.',
  'Verify all pre-departure documents before student departs.',
  'Student is abroad — collect Certificate of Arrival and monitor.',
  'Process credit transfer and close the student file.',
];

/* ─── BASE DOCUMENTS BY STEP ────────────────────────────── */
const BASE_DOCUMENTS = [
  // Step 0 — Application
  { name: 'Passport / ID Copy',           step: 0, icon: 'draft',             baseStatus: 'approved' },
  { name: 'Academic Transcript',           step: 0, icon: 'school',            baseStatus: 'approved' },
  { name: 'Motivation Letter',             step: 0, icon: 'edit_note',         baseStatus: 'pending'  },
  // Step 1 — Nomination R1 (internal action, no docs)
  // Step 2 — Nomination R2
  { name: 'Acceptance Letter',             step: 2, icon: 'mark_email_read',   baseStatus: 'missing'  },
  { name: 'Grant Agreement (Erasmus)',     step: 2, icon: 'handshake',         baseStatus: 'missing'  },
  // Step 3 — Pre-departure
  { name: 'Health Insurance Certificate', step: 3, icon: 'medical_services',  baseStatus: 'missing'  },
  { name: 'Visa / Residence Permit',      step: 3, icon: 'flight_takeoff',    baseStatus: 'missing'  },
  { name: 'Housing Confirmation',          step: 3, icon: 'real_estate_agent', baseStatus: 'missing'  },
  // Step 4 — During Exchange
  { name: 'Certificate of Arrival',        step: 4, icon: 'where_to_vote',    baseStatus: 'missing'  },
  // Step 5 — Return
  { name: 'Certificate of Departure',      step: 5, icon: 'logout',           baseStatus: 'missing'  },
  { name: 'Official Transcript of Records',step: 5, icon: 'description',      baseStatus: 'missing'  },
  { name: 'Credit Transfer Application',   step: 5, icon: 'swap_horiz',       baseStatus: 'missing'  },
];

const STATUS_STYLES = {
  'Departed':          'bg-slate-100 text-slate-600',
  'Partner Contacted': 'bg-amber-100 text-amber-700',
  'Nominated':         'bg-blue-100 text-blue-700',
  'Applied':           'bg-slate-200 text-slate-600',
  'Returned':          'bg-slate-100 text-slate-500',
};

function statusConfig(status) {
  switch (status) {
    case 'uploaded':
    case 'approved':  return { label: 'Approved',          style: 'bg-emerald-100 text-emerald-700', icon: 'check_circle'    };
    case 'pending':   return { label: 'Pending Review',    style: 'bg-amber-100 text-amber-700',     icon: 'hourglass_empty' };
    case 'rejected':  return { label: 'Changes Requested', style: 'bg-red-100 text-red-600',         icon: 'cancel'          };
    default:          return { label: 'Missing',           style: 'bg-red-100 text-red-700',         icon: 'cancel'          };
  }
}

/* ─── HORIZONTAL STEPPER COMPONENT ─────────────────────── */
function HorizontalStepper({ steps, currentIndex, children }) {
  const n         = steps.length;
  const halfCell  = 100 / (2 * n);
  const lineSpan  = 100 - 2 * halfCell;
  const progFrac  = currentIndex <= 0 ? 0 : currentIndex / (n - 1);
  const progWidth = progFrac * lineSpan;
  const pct       = Math.round(progFrac * 100);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px] text-indigo-600">route</span>
          <h2 className="font-display text-base font-semibold text-[#0b1c30]">Process Status</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{currentIndex} of {n} phases completed</span>
          <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{pct}%</span>
        </div>
      </div>

      <div className="relative flex">
        <div className="absolute bg-slate-100 rounded-full"
          style={{ top: '16px', left: `${halfCell}%`, width: `${lineSpan}%`, height: '2px' }} />
        {progWidth > 0 && (
          <div className="absolute rounded-full transition-all duration-500 overflow-hidden"
            style={{ top: '16px', left: `${halfCell}%`, width: `${progWidth}%`, height: '2px', backgroundColor: '#4343d5' }}>
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.9) 50%, transparent 100%)',
              width: '40%',
              animation: 'progress-shimmer 2.8s ease-in-out infinite',
            }} />
          </div>
        )}
        <style>{`
          @keyframes progress-shimmer {
            0%   { transform: translateX(-100%); }
            100% { transform: translateX(350%); }
          }
        `}</style>
        {steps.map((step, i) => {
          const isDone    = i < currentIndex;
          const isCurrent = i === currentIndex;
          return (
            <div key={i} className="flex-1 flex flex-col items-center">
              <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                isDone    ? 'bg-indigo-600 border-indigo-600' :
                isCurrent ? 'bg-white border-indigo-600'      :
                            'bg-white border-slate-200'
              }`}>
                {isDone ? (
                  <span className="material-symbols-outlined text-white text-[14px]">check</span>
                ) : isCurrent ? (
                  <>
                    <span className="absolute inset-0 rounded-full bg-indigo-400 animate-ping opacity-30" />
                    <div className="w-3 h-3 bg-indigo-600 rounded-full" />
                  </>
                ) : (
                  <span className="text-[10px] font-bold text-slate-300">{i + 1}</span>
                )}
              </div>
              <div className="mt-2 text-center px-0.5 w-full">
                <p className={`text-[11px] font-semibold leading-tight ${
                  isDone ? 'text-slate-600' : isCurrent ? 'text-indigo-700' : 'text-slate-300'
                }`}>{step.label}</p>
                <p className={`text-[10px] mt-0.5 leading-tight ${isCurrent ? 'text-indigo-500 font-medium' : 'text-slate-300'}`}>
                  {isCurrent ? '● Current' : step.sub}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {children && <div className="mt-5 space-y-2">{children}</div>}
    </div>
  );
}

/* ─── OUTGOING PROFILE COMPONENT ───────────────────────── */
export default function OutgoingProfile() {
  const navigate = useNavigate();
  const { getUpload, uploadDocument, approveDocument, rejectDocument, viewDocument } = useDocuments();
  const { note, setNote, save, savedAt, justSaved } = useLocalNote(STUDENT_ID);

  const [manualStep, setManualStep] = useState(() => {
    const saved = localStorage.getItem(STEP_STORAGE);
    return saved !== null ? parseInt(saved, 10) : null;
  });

  const currentStepIndex = manualStep ?? 3;
  const canAdvance = currentStepIndex < STEPS.length - 1;

  const advanceStep = () => {
    const next = currentStepIndex + 1;
    if (next >= STEPS.length) return;
    localStorage.setItem(STEP_STORAGE, next);
    setManualStep(next);
  };

  const computedStatus = STEP_TO_STATUS[currentStepIndex] ?? 'Applied';
  useEffect(() => {
    localStorage.setItem(STATUS_KEY, computedStatus);
  }, [computedStatus]);

  /* Build docs with effective status from DocumentContext */
  const docs = BASE_DOCUMENTS.map(doc => {
    const upload = getUpload(STUDENT_ID, doc.name);
    const effectiveStatus = upload ? upload.status : doc.baseStatus;
    return { ...doc, effectiveStatus, upload };
  });

  const approvedCount = docs.filter(d => ['uploaded', 'approved'].includes(d.effectiveStatus)).length;
  const pendingCount  = docs.filter(d => d.effectiveStatus === 'pending').length;

  /* Group docs by step */
  const docsByStep = STEPS.map((step, i) => ({
    stepIndex: i,
    stepLabel: step.label,
    docs: docs.filter(d => d.step === i),
  }));

  const note_text = STEP_NOTES[currentStepIndex];

  return (
    <Layout>
      {/* Back */}
      <button onClick={() => navigate('/outgoing')}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-6 transition-colors group">
        <span className="material-symbols-outlined text-[18px] group-hover:-translate-x-0.5 transition-transform">arrow_back</span>
        Outgoing Students
      </button>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-display text-4xl font-bold text-[#0b1c30]">Tom Baker</h1>
          <p className="text-[#464555] mt-2 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-[16px]">location_on</span>
            Imperial College London, UK
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 rounded-full border border-slate-200 text-sm text-slate-600 font-medium">Exchange 2026/27</span>
          <span className="px-3 py-1 rounded-full border border-slate-200 text-sm text-slate-600 font-medium">MIM</span>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[computedStatus] ?? 'bg-indigo-100 text-indigo-700'}`}>
            {computedStatus}
          </span>
        </div>
      </div>

      {/* Stepper */}
      <HorizontalStepper steps={STEPS} currentIndex={currentStepIndex}>
        {note_text && (
          <div className="flex items-start gap-2.5 px-4 py-3 bg-indigo-50 rounded-xl border border-indigo-100">
            <span className="material-symbols-outlined text-indigo-500 text-[16px] flex-shrink-0 mt-0.5">info</span>
            <p className="text-xs text-indigo-700 leading-relaxed">
              <span className="font-semibold">{STEPS[currentStepIndex].label}: </span>{note_text}
            </p>
          </div>
        )}
        {canAdvance && (
          <button onClick={advanceStep}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors">
            <span className="text-xs font-semibold flex items-center gap-2">
              <span className="material-symbols-outlined text-[15px]">arrow_circle_right</span>
              Mark as: <span className="text-indigo-800">{STEPS[currentStepIndex + 1]?.label}</span>
            </span>
            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
          </button>
        )}
        {!canAdvance && (
          <div className="flex items-center gap-2.5 px-4 py-3 bg-emerald-50 rounded-xl border border-emerald-200">
            <span className="material-symbols-outlined text-emerald-500 text-[16px]">verified</span>
            <p className="text-xs font-semibold text-emerald-700">Exchange completed — all phases done.</p>
          </div>
        )}
      </HorizontalStepper>

      {/* Two-column content */}
      <div className="grid grid-cols-12 gap-6">

        {/* Left */}
        <div className="col-span-8 space-y-6">

          {/* Student Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">person</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Student Information</h2>
            </div>
            <div className="p-6 grid grid-cols-2 gap-x-8 gap-y-5">
              <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Full Name</p><p className="text-sm text-[#0b1c30] font-medium">Tom Baker</p></div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Email</p>
                <a href="mailto:t.baker@esmt.org" className="text-sm text-indigo-600 font-medium hover:underline flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[14px]">mail</span>t.baker@esmt.org
                </a>
              </div>
              <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Destination</p><p className="text-sm text-[#0b1c30] font-medium">Imperial College London</p></div>
              <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Nationality</p><p className="text-sm text-[#0b1c30] font-medium">🇬🇧 UK</p></div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Program</p>
                <span className="inline-block text-xs font-semibold bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-full">Master in Management</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Exchange Period</p>
                <p className="text-sm text-[#0b1c30] font-medium flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[14px] text-slate-400">calendar_month</span>
                  Sep 2026 — Feb 2027
                </p>
              </div>
            </div>
          </div>

          {/* Documents — grouped by step */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">folder_open</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Documents</h2>
              </div>
              <div className="flex items-center gap-2">
                {pendingCount > 0 && (
                  <span className="flex items-center gap-1 text-xs font-semibold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">
                    <span className="material-symbols-outlined text-[13px]">notifications</span>
                    {pendingCount} pending review
                  </span>
                )}
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                  approvedCount === docs.length ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                }`}>
                  {approvedCount} of {docs.length} Complete
                </span>
              </div>
            </div>

            <div className="p-4 space-y-1">
              {docsByStep.map(group => {
                const isLocked  = group.stepIndex > currentStepIndex;
                const isCurrent = group.stepIndex === currentStepIndex;
                const isDone    = group.stepIndex < currentStepIndex;

                return (
                  <div key={group.stepIndex}>
                    {/* Step header */}
                    <div className={`flex items-center gap-2 px-2 pt-4 pb-2 ${group.stepIndex === 0 ? 'pt-2' : ''}`}>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        isDone    ? 'bg-indigo-100 text-indigo-700' :
                        isCurrent ? 'bg-indigo-600 text-white'      :
                                    'bg-slate-100 text-slate-400'
                      }`}>Step {group.stepIndex + 1}</span>
                      <span className={`text-xs font-semibold ${
                        isDone ? 'text-slate-500' : isCurrent ? 'text-indigo-700' : 'text-slate-300'
                      }`}>{group.stepLabel}</span>
                      {isDone && <span className="material-symbols-outlined text-indigo-400 text-[14px]">check_circle</span>}
                      {isLocked && <span className="material-symbols-outlined text-slate-300 text-[14px]">lock</span>}
                    </div>

                    {/* No docs for this step */}
                    {group.docs.length === 0 && (
                      <div className="mx-2 mb-2 px-4 py-3 rounded-xl bg-slate-50 border border-slate-100">
                        <p className="text-xs text-slate-400 italic">Internal process step — no documents required.</p>
                      </div>
                    )}

                    {/* Doc rows */}
                    {group.docs.map(doc => {
                      const cfg       = statusConfig(doc.effectiveStatus);
                      const isPending = doc.effectiveStatus === 'pending';
                      return (
                        <div key={doc.name}
                          className={`flex items-center justify-between px-4 py-3.5 rounded-xl mx-2 mb-1 transition-colors ${
                            isLocked    ? 'opacity-40' :
                            isPending   ? 'bg-amber-50 border border-amber-100' :
                                          'hover:bg-slate-50'
                          }`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <span className="material-symbols-outlined text-[16px] text-slate-500">{doc.icon}</span>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-[#0b1c30]">{doc.name}</span>
                              {isPending && doc.upload && (
                                <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[11px]">attach_file</span>
                                  {doc.upload.fileName} · uploaded {new Date(doc.upload.uploadedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.style}`}>
                              <span className="material-symbols-outlined text-[14px]">{cfg.icon}</span>
                              {cfg.label}
                            </div>
                            {(doc.effectiveStatus === 'missing' || doc.effectiveStatus === 'rejected') && !isLocked && (
                              <label className="cursor-pointer text-xs font-medium text-indigo-600 border border-indigo-200 px-2.5 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors flex items-center gap-1">
                                <span className="material-symbols-outlined text-[14px]">upload</span>Upload
                                <input type="file" className="hidden" onChange={e => {
                                  const file = e.target.files[0];
                                  if (file) uploadDocument(STUDENT_ID, 'Tom Baker', doc.name, file);
                                }} />
                              </label>
                            )}
                            {isPending && doc.upload && !isLocked && (
                              <>
                                <button onClick={() => viewDocument(doc.upload)}
                                  className="text-xs font-medium text-slate-600 border border-slate-200 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-colors flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[14px]">open_in_new</span>View
                                </button>
                                <button onClick={() => approveDocument(doc.upload.id)}
                                  className="text-xs font-medium text-emerald-700 border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[14px]">check</span>Approve
                                </button>
                                <button onClick={() => rejectDocument(doc.upload.id)}
                                  className="text-xs font-medium text-red-600 border border-red-200 bg-red-50 px-2.5 py-1.5 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[14px]">close</span>Reject
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="col-span-4 space-y-6">

          {/* Admin Notes */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">notes</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Admin Notes</h2>
              </div>
              {savedAt && (
                <p className="text-[11px] text-slate-400 flex items-center gap-1">
                  <span className="material-symbols-outlined text-[12px]">schedule</span>Saved {savedAt}
                </p>
              )}
            </div>
            <div className="p-5">
              <p className="text-xs text-slate-400 mb-3">Internal notes — visible only to university staff.</p>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={4}
                placeholder="Add a note about this student's process..."
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 text-[#0b1c30] placeholder-slate-400 transition"
              />
              <button onClick={save}
                className={`mt-3 w-full py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  justSaved ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'border border-slate-200 text-[#464555] hover:bg-slate-50'
                }`}>
                <span className="material-symbols-outlined text-[16px]">{justSaved ? 'check_circle' : 'save'}</span>
                {justSaved ? 'Saved!' : 'Save Note'}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Actions</h2>
            </div>
            <div className="p-5 space-y-3">
              <button className="w-full py-2.5 border border-indigo-300 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[16px]">send</span>Send Reminder to Partner
              </button>
              <button className="w-full py-2.5 bg-[#4343d5] text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[16px]">mail</span>Contact Student
              </button>
            </div>
          </div>

          {/* IO Contacts */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">contacts</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">IO Contacts</h2>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">ESMT IO Manager</p>
                <p className="text-sm font-medium text-[#0b1c30]">Julia Klein</p>
                <a href="mailto:j.klein@esmt.org" className="text-xs text-indigo-600 hover:underline">j.klein@esmt.org</a>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Destination IO</p>
                <p className="text-sm font-medium text-[#0b1c30]">Imperial — Int'l Students Office</p>
                <a href="mailto:international@imperial.ac.uk" className="text-xs text-indigo-600 hover:underline">international@imperial.ac.uk</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
